// App-wide runtime config (currently just the deployment lane).
//
// Fetched once on boot from GET /api/config and exposed via context so
// any component can badge the environment without re-fetching. The fetch
// is best-effort: if it fails we simply don't show the environment
// banner. Access control on the stage lane is enforced server-side
// (middleware/stage-gate.ts), never by this value — so a failed fetch
// can never "open" a gated environment.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import * as configApi from '../api/config.js';
import type { AppEnvironment } from '../api/config.js';

type ConfigState = {
  // null until the boot fetch resolves (or if it fails).
  environment: AppEnvironment | null;
  // The active lane's Stripe publishable key, delivered at runtime so the
  // bundle stays lane-agnostic. null until loaded (or if the fetch failed);
  // '' when Stripe isn't configured for the lane.
  stripe_publishable_key: string | null;
  // Release v1 pricing lockdown. When true, the pricing flow is replaced by a
  // contact-sales panel. Defaults to TRUE (locked, fail-safe) until the boot
  // fetch resolves and whenever the backend omits the field, so the portal never
  // flashes a price surface before confirming the lane is unlocked.
  pricing_locked: boolean;
  // Analytics/APM keys served at runtime (ADR-014). null until the boot fetch
  // resolves (or if it fails), and '' in any lane where the backend env var is
  // unset — the analytics chokepoint treats a missing/empty key as "off", so
  // analytics is simply absent locally with no code branch.
  clarity_project_id: string | null;
  app_insights_connection_string: string | null;
  // True once the boot fetch has settled (resolved OR failed). Lets payment
  // UI distinguish "still loading config" from "loaded, Stripe unconfigured".
  loaded: boolean;
};

const ConfigContext = createContext<ConfigState>({
  environment: null,
  stripe_publishable_key: null,
  pricing_locked: true,
  clarity_project_id: null,
  app_insights_connection_string: null,
  loaded: false,
});

export function ConfigProvider({ children }: { children: ReactNode }): JSX.Element {
  const [environment, setEnvironment] = useState<AppEnvironment | null>(null);
  const [stripeKey, setStripeKey] = useState<string | null>(null);
  // Start LOCKED (fail-safe): never flash a price surface before we've confirmed
  // the lane is unlocked. Flipped to false only when the fetch explicitly says so.
  const [pricingLocked, setPricingLocked] = useState(true);
  const [clarityProjectId, setClarityProjectId] = useState<string | null>(null);
  const [appInsightsConnectionString, setAppInsightsConnectionString] = useState<string | null>(
    null,
  );
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const cfg = await configApi.getConfig();
        if (!cancelled) {
          setEnvironment(cfg.environment);
          setStripeKey(cfg.stripe_publishable_key);
          // Locked unless the backend explicitly reports pricing_locked=false
          // (an older backend omits the field ⇒ stays locked).
          setPricingLocked(cfg.pricing_locked !== false);
          setClarityProjectId(cfg.clarity_project_id ?? '');
          setAppInsightsConnectionString(cfg.app_insights_connection_string ?? '');
        }
      } catch {
        // Non-fatal — leave the banner hidden if config can't load. Payment
        // UI keys off `loaded` and a null key to show its not-configured state.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ConfigContext.Provider
      value={{
        environment,
        stripe_publishable_key: stripeKey,
        pricing_locked: pricingLocked,
        clarity_project_id: clarityProjectId,
        app_insights_connection_string: appInsightsConnectionString,
        loaded,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export function useAppEnvironment(): AppEnvironment | null {
  return useContext(ConfigContext).environment;
}

// Release v1 pricing lockdown. True (locked) until the boot config fetch says
// otherwise — the portal replaces the price flow with a contact-sales panel.
export function usePricingLocked(): boolean {
  return useContext(ConfigContext).pricing_locked;
}

// Runtime Stripe config for the payment card field. `loaded` is false until
// the boot config fetch settles, so the UI can show a loading state instead
// of flashing "Stripe is not configured" before the key arrives.
export function useStripeConfig(): { publishable_key: string | null; loaded: boolean } {
  const { stripe_publishable_key, loaded } = useContext(ConfigContext);
  return { publishable_key: stripe_publishable_key, loaded };
}

// Runtime-served analytics/APM keys (Microsoft Clarity + Azure Application
// Insights), camelCased for the analytics chokepoint (lib/analytics). Both null
// until the boot config fetch resolves, and empty in any lane where the backend
// env var is unset — the chokepoint treats a missing/empty key as "off".
export function useAnalyticsConfig(): {
  clarityProjectId: string | null;
  appInsightsConnectionString: string | null;
} {
  const { clarity_project_id, app_insights_connection_string } = useContext(ConfigContext);
  return {
    clarityProjectId: clarity_project_id,
    appInsightsConnectionString: app_insights_connection_string,
  };
}
